import { Button, Icon } from "@xinghai/ui";

export type MobileOs = "ios" | "android" | "desktop";

export function detectOs(ua: string = navigator.userAgent): MobileOs {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

const STEPS: Record<MobileOs, string[]> = {
  ios: [
    "点地址栏左边的「ᴀA」按钮。",
    "选「网站设置」。",
    "把「麦克风」改成「允许」。",
    "回到这里，再点一次录音键。",
    "如果还是不行：设置 → Safari → 麦克风 → 允许。",
  ],
  android: [
    "点地址栏左边的锁图标。",
    "选「权限」或「网站设置」。",
    "把「麦克风」打开。",
    "回到这里，再点一次录音键。",
  ],
  desktop: ["点地址栏左边的锁图标。", "把「麦克风」设为「允许」。", "刷新页面，再点一次录音键。"],
};

const OS_LABEL: Record<MobileOs, string> = {
  ios: "iPhone / iPad（Safari）",
  android: "安卓手机（Chrome）",
  desktop: "电脑浏览器",
};

/** Inline illustration: the address bar and the control the student must tap. */
function AddressBarArt({ os }: { os: MobileOs }) {
  return (
    <svg
      className="xz-michelp__art"
      viewBox="0 0 320 96"
      role="img"
      aria-label="浏览器地址栏示意图"
    >
      <rect x="8" y="16" width="304" height="40" rx="12" fill="var(--gz-surface-interactive)" />
      <rect
        x="16"
        y="24"
        width="40"
        height="24"
        rx="8"
        fill="var(--gz-brand-soft)"
        stroke="var(--gz-action-primary)"
        strokeWidth="2"
      />
      <text
        x="36"
        y="41"
        textAnchor="middle"
        fontSize="13"
        fill="var(--gz-action-primary)"
        fontFamily="inherit"
      >
        {os === "ios" ? "ᴀA" : "🔒"}
      </text>
      <rect x="64" y="28" width="230" height="16" rx="8" fill="var(--gz-border)" />
      <path
        d="M36 64 L36 84 M30 78 L36 84 L42 78"
        stroke="var(--gz-action-primary)"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface MicHelpProps {
  /** Message from the recorder (permission denied / no device / busy). */
  message: string;
  onRetry: () => void;
  os?: MobileOs;
}

/** docs/UX.md §1.3 — 麦克风被拒：分 iOS / Android 两张图文说明. */
export function MicHelp({ message, onRetry, os = detectOs() }: MicHelpProps) {
  return (
    <section className="xz-michelp" role="alert">
      <p className="gz-notice gz-notice--error">
        <Icon id="ui-error" size={20} />
        {message}
      </p>
      <h2 className="gz-type-ui">在{OS_LABEL[os]}里这样打开</h2>
      <AddressBarArt os={os} />
      <ol className="xz-michelp__steps gz-type-reading">
        {STEPS[os].map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      <Button variant="primary" size="lg" block onClick={onRetry}>
        <Icon id="ui-refresh" size={20} /> 我打开了，再试一次
      </Button>
    </section>
  );
}
